import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  IconButton, Stack, Chip, Alert, LinearProgress, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Webhook as WebhookIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { webhooksApi } from '../api/services';
import type { WebhookSubscription } from '../types/models';

const WEBHOOK_EVENTS = [
  'PlanProposed',
  'PlanApproved',
  'PlanClosed',
  'WorkItemCreated',
  'TranscriptAnalysed',
];

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState<{ url: string; event: string; secret: string }>({
    url: '',
    event: WEBHOOK_EVENTS[0],
    secret: '',
  });
  const [toggling, setToggling] = useState<string | null>(null);

  const loadWebhooks = async () => {
    setLoading(true);
    setError(null);
    try {
      setWebhooks(await webhooksApi.getAll());
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWebhooks(); }, []);

  const handleCreate = async () => {
    if (!newWebhook.url || !newWebhook.event) return;
    try {
      await webhooksApi.create({
        url: newWebhook.url,
        event: newWebhook.event,
        secret: newWebhook.secret || undefined,
      });
      setDialogOpen(false);
      setNewWebhook({ url: '', event: WEBHOOK_EVENTS[0], secret: '' });
      loadWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create webhook');
    }
  };

  const handleToggle = async (webhook: WebhookSubscription) => {
    setToggling(webhook.id);
    try {
      await webhooksApi.update(webhook.id, { isActive: !webhook.isActive });
      loadWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to toggle webhook');
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this webhook subscription?')) return;
    try {
      await webhooksApi.delete(id);
      loadWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete webhook');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WebhookIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h4">Webhooks</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage webhook subscriptions for external integrations
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Webhook
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && webhooks.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <WebhookIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>No webhook subscriptions</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Create a webhook to receive event notifications from Cadence.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Webhook
          </Button>
        </Card>
      )}

      {webhooks.length > 0 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Failures</TableCell>
                    <TableCell>Last Triggered</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.map((wh) => (
                    <TableRow key={wh.id} hover>
                      <TableCell>
                        <Chip
                          label={wh.event}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={wh.url} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.primary',
                            }}
                          >
                            {wh.url}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch
                            size="small"
                            checked={wh.isActive}
                            onChange={() => handleToggle(wh)}
                            disabled={toggling === wh.id}
                          />
                          <Chip
                            label={wh.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={wh.isActive ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ height: 22 }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" color={wh.failureCount >= 5 ? 'error.main' : 'text.secondary'}>
                            {wh.failureCount}
                          </Typography>
                          {wh.failureCount >= 5 && (
                            <Tooltip title="5+ consecutive failures — webhook may be unreachable">
                              <WarningIcon sx={{ fontSize: 18, color: 'error.main' }} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {wh.lastTriggeredAt
                            ? format(new Date(wh.lastTriggeredAt), 'dd MMM yyyy HH:mm')
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(wh.id)}
                          sx={{ color: 'error.main' }}
                          title="Delete webhook"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Add Webhook Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Webhook Subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Webhook URL"
              fullWidth
              value={newWebhook.url}
              onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://example.com/webhook"
              type="url"
            />
            <FormControl fullWidth>
              <InputLabel>Event</InputLabel>
              <Select
                value={newWebhook.event}
                label="Event"
                onChange={(e) => setNewWebhook(prev => ({ ...prev, event: e.target.value }))}
              >
                {WEBHOOK_EVENTS.map(ev => (
                  <MenuItem key={ev} value={ev}>{ev}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Secret (optional)"
              fullWidth
              value={newWebhook.secret}
              onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e.target.value }))}
              placeholder="Shared secret for signature verification"
              type="password"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newWebhook.url || !newWebhook.event}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
